import { Capacitor, registerPlugin } from '@capacitor/core';
import { ATHENA_VERSION_INFO } from '../config/version';

// These proxies are inert on web and on APKs that predate Fase 5.
const NativeDeviceContext = registerPlugin('AthenaDeviceContext');
const NativeAppInfo = registerPlugin('AthenaAppInfo');

const noop = () => {};
const SAFE_OWNER_ID = /^[A-Za-z0-9._:-]{1,180}$/;
const APP_STATES = new Set(['foreground', 'background', 'unknown']);
const NETWORK_TYPES = new Set(['wifi', 'cellular', 'none', 'unknown', 'ethernet', 'bluetooth', 'other']);
const AUDIO_OUTPUTS = new Set(['wired', 'bluetooth', 'speaker', 'none', 'unknown']);
// Exact Fase 5 registration vocabulary. Never forward arbitrary strings from
// a native plugin, otherwise an APK upgrade could make registration fail.
const WORKER_NEGOTIABLE_CAPABILITIES = new Set([
  'native_voice_v1', 'native_push_v1', 'calendar_v1', 'files_v1', 'share_v1',
  'device_context_v1', 'static_shortcuts_v1', 'privacy_safe_widget_v1',
  'quick_settings_tile_v1', 'quick_capture_v1',
]);

function isNativeAndroid() {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

function isPluginAvailable(name) {
  try {
    return isNativeAndroid() && Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
}

function safeString(value, maximum = 120) {
  return typeof value === 'string'
    ? value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, maximum)
    : '';
}

function safeBoolean(value) {
  return typeof value === 'boolean' ? value : null;
}

function normalizeEnabledSignals(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return Object.freeze({
    battery: source.battery === true,
    network: source.network === true,
    audio: source.audio === true,
    appState: source.appState === true,
  });
}

function enumValue(value, allowed, fallback) {
  const normalized = safeString(value, 40).toLowerCase().replace(/[\s-]+/g, '_');
  return allowed.has(normalized) ? normalized : fallback;
}

function safePercentage(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const percent = numeric >= 0 && numeric <= 1 ? numeric * 100 : numeric;
  return percent >= 0 && percent <= 100 ? Math.round(percent) : null;
}

function safeTimestamp(value) {
  // Android transports `collectedAt` as epoch milliseconds. Date.parse() does
  // not reliably parse a numeric epoch (especially across WebView versions),
  // so preserve a sane native millisecond timestamp before trying ISO text.
  const numeric = typeof value === 'number'
    ? value
    : typeof value === 'string' && /^\d{11,16}$/.test(value.trim())
      ? Number(value)
      : NaN;
  if (Number.isInteger(numeric) && numeric >= 946684800000 && numeric <= 4102444800000) {
    return new Date(numeric).toISOString();
  }
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : new Date().toISOString();
}

function browserLocale() {
  try { return Intl.DateTimeFormat().resolvedOptions().locale || navigator.language || 'pt-BR'; } catch { return 'pt-BR'; }
}

function browserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

function browserSnapshot() {
  const connected = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  const state = typeof document === 'undefined' || document.visibilityState !== 'hidden' ? 'foreground' : 'background';
  return Object.freeze({
    platform: 'web',
    appState: state,
    network: Object.freeze({ connected, type: connected ? 'unknown' : 'none', metered: null }),
    battery: null,
    charging: null,
    powerSaver: null,
    audioOutput: 'unknown',
    timezone: browserTimezone(),
    locale: browserLocale(),
    capabilities: Object.freeze({
      deviceContext: false,
      battery: false,
      network: typeof navigator !== 'undefined',
      audioOutput: false,
      nativeLifecycle: false,
    }),
    collectedAt: new Date().toISOString(),
  });
}

function normalizeCapabilities(raw = {}) {
  const value = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const names = new Set((Array.isArray(raw)
    ? raw
    : Array.isArray(value.capabilities)
      ? value.capabilities
      : Array.isArray(value.features) ? value.features : [])
    .filter((entry) => typeof entry === 'string')
    .map((entry) => entry.trim().toLowerCase().replace(/[\s-]+/g, '_')));
  const pluginAvailable = isPluginAvailable('AthenaDeviceContext');
  return Object.freeze({
    deviceContext: value.deviceContext === true || value.available === true || names.has('device_context') || names.has('device_context_v1') || pluginAvailable,
    // These describe bounded state fields only. They never describe a
    // microphone stream, raw audio, GPS, app list, or background tracking.
    battery: value.battery === true || value.batteryState === true || names.has('battery') || names.has('battery_context') || names.has('battery_context_v1') || pluginAvailable,
    network: value.network === true || value.networkState === true || names.has('network') || names.has('network_context') || names.has('network_context_v1') || pluginAvailable,
    audioOutput: value.audioOutput === true || value.audio === true || value.headset === true || names.has('audio') || names.has('audio_context') || names.has('audio_context_v1') || pluginAvailable,
    nativeLifecycle: value.nativeLifecycle === true || pluginAvailable,
  });
}

function normalizeNativeSnapshot(result) {
  const envelope = result && typeof result === 'object' ? result : {};
  const value = envelope.snapshot && typeof envelope.snapshot === 'object' ? envelope.snapshot : envelope;
  const network = value.network && typeof value.network === 'object' ? value.network : {};
  const rawAppState = safeString(value.appState || value.app_state, 40).toLowerCase();
  const appState = rawAppState === 'active'
    ? 'foreground'
    : enumValue(rawAppState, APP_STATES, 'unknown');
  const connected = safeBoolean(network.connected ?? value.networkConnected ?? value.network_connected);
  const rawNetworkType = safeString(network.type || network.connectionType || value.networkType, 40)
    .toLowerCase().replace(/[\s-]+/g, '_');
  const type = rawNetworkType === 'vpn'
    ? 'other'
    : enumValue(rawNetworkType, NETWORK_TYPES, connected === false ? 'none' : 'unknown');
  const timezone = safeString(value.timezone, 80) || browserTimezone();
  const locale = safeString(value.locale, 48) || browserLocale();
  return Object.freeze({
    platform: 'android',
    appState,
    network: Object.freeze({
      connected: connected !== null ? connected : type !== 'none',
      type,
      metered: safeBoolean(network.metered ?? value.networkMetered ?? value.network_metered),
    }),
    battery: safePercentage(value.battery ?? value.batteryLevel ?? value.battery_level ?? value.batteryPercent ?? value.battery_percent),
    charging: safeBoolean(value.charging ?? value.isCharging ?? value.is_charging),
    powerSaver: safeBoolean(value.powerSaver ?? value.power_saver),
    audioOutput: (() => {
      const rawAudio = value.audioOutput ?? value.audio_output ?? value.audioCategory ?? value.audio_category;
      const normalizedAudio = safeString(rawAudio, 40).toLowerCase().replace(/[\s-]+/g, '_');
      if (normalizedAudio === 'wired_headset') return 'wired';
      if (normalizedAudio === 'usb' || normalizedAudio === 'other') return 'unknown';
      return enumValue(normalizedAudio, AUDIO_OUTPUTS, 'unknown');
    })(),
    timezone,
    locale,
    capabilities: normalizeCapabilities(envelope.capabilities || value.capabilities),
    collectedAt: safeTimestamp(value.collectedAt || value.collected_at),
  });
}

async function call(plugin, methodNames, args = undefined) {
  for (const method of methodNames) {
    try {
      if (typeof plugin?.[method] !== 'function') continue;
      return args === undefined ? await plugin[method]() : await plugin[method](args);
    } catch {
      // Compatibility aliases keep an old installed APK a harmless no-op.
    }
  }
  return null;
}

export function isAthenaDeviceContextAvailable() {
  return isPluginAvailable('AthenaDeviceContext');
}

export function isAthenaAppInfoAvailable() {
  return isPluginAvailable('AthenaAppInfo');
}

export async function getAthenaDeviceContextCapabilities() {
  if (!isAthenaDeviceContextAvailable()) return browserSnapshot().capabilities;
  const result = await call(NativeDeviceContext, ['getCapabilities', 'getStatus']);
  return normalizeCapabilities(result?.capabilities || result);
}

/** Reads only a current bounded state; it never starts observing or requests a permission. */
export async function getAthenaDeviceContextSnapshot() {
  if (!isAthenaDeviceContextAvailable()) return browserSnapshot();
  const result = await call(NativeDeviceContext, ['getSnapshot']);
  return normalizeNativeSnapshot(result);
}

/**
 * Native consent is intentionally separate from Android runtime permissions.
 * Calling this is an explicit UI action; ordinary app boot never enables it.
 */
export async function setAthenaDeviceContextConsent(enabled) {
  const granted = enabled === true;
  if (!isAthenaDeviceContextAvailable()) return { available: false, enabled: false };
  const result = await call(NativeDeviceContext, ['setConsent'], { enabled: granted });
  return { available: true, enabled: result?.consentEnabled === true || result?.enabled === true || (granted && result?.ok === true) };
}

/**
 * Narrows native collection to the user-approved categories. Older APKs do
 * not expose this method; they receive a harmless no-op and the frontend
 * still masks disabled fields before retaining or sending a snapshot.
 */
export async function setAthenaDeviceContextEnabledSignals(signals = {}) {
  const enabledSignals = normalizeEnabledSignals(signals);
  if (!isAthenaDeviceContextAvailable()) return { available: false, applied: false, enabledSignals };
  const result = await call(NativeDeviceContext, ['setEnabledSignals'], enabledSignals);
  return {
    available: true,
    applied: result?.ok === true || result?.applied === true || result?.updated === true,
    enabledSignals,
  };
}

export async function startAthenaDeviceContextObserving() {
  if (!isAthenaDeviceContextAvailable()) return { available: false, started: false };
  const result = await call(NativeDeviceContext, ['startObserving']);
  return { available: true, started: result?.started === true || result?.ok === true };
}

export async function stopAthenaDeviceContextObserving() {
  if (!isAthenaDeviceContextAvailable()) return { available: false, stopped: false };
  const result = await call(NativeDeviceContext, ['stopObserving']);
  return { available: true, stopped: result?.stopped === true || result?.ok === true };
}

export async function observeAthenaDeviceContext(listener) {
  if (!isAthenaDeviceContextAvailable() || typeof listener !== 'function') return noop;
  try {
    const handle = await NativeDeviceContext.addListener('contextEvent', listener);
    return () => {
      try { void handle?.remove?.(); } catch { /* WebView can already be gone. */ }
    };
  } catch {
    // `deviceContextChanged` is the compatibility event used by the first F5
    // APKs. Only subscribe when the canonical event was unavailable.
    try {
      const handle = await NativeDeviceContext.addListener('deviceContextChanged', listener);
      return () => {
        try { void handle?.remove?.(); } catch { /* noop */ }
      };
    } catch {
      return noop;
    }
  }
}

export async function bindAthenaDeviceContextOwner(ownerId) {
  const safeOwnerId = safeString(ownerId, 180);
  if (!SAFE_OWNER_ID.test(safeOwnerId) || !isNativeAndroid()) return { available: false, ownerChanged: false };
  const [contextResult, infoResult] = await Promise.all([
    isAthenaDeviceContextAvailable() ? call(NativeDeviceContext, ['bindOwner'], { ownerId: safeOwnerId }) : null,
    isAthenaAppInfoAvailable() ? call(NativeAppInfo, ['bindOwner'], { ownerId: safeOwnerId }) : null,
  ]);
  return {
    available: Boolean(contextResult || infoResult),
    ownerChanged: contextResult?.ownerChanged === true || infoResult?.ownerChanged === true,
  };
}

/** Clear only user-scoped device context/install metadata, never Android permissions. */
export async function clearAthenaDeviceContextForUser(ownerId = '') {
  if (!isNativeAndroid()) return { available: false, cleared: false };
  const safeOwnerId = safeString(ownerId, 180);
  const ownerPayload = SAFE_OWNER_ID.test(safeOwnerId) ? { ownerId: safeOwnerId } : null;
  if (ownerPayload) {
    // During automatic logout an old APK must be a safe no-op rather than
    // falling back to a global clear that could target the next account.
    const [contextResult, infoResult] = await Promise.all([
      isAthenaDeviceContextAvailable() ? call(NativeDeviceContext, ['clearForOwner'], ownerPayload) : null,
      isAthenaAppInfoAvailable() ? call(NativeAppInfo, ['clearForOwner'], ownerPayload) : null,
    ]);
    return {
      available: Boolean(contextResult || infoResult),
      cleared: contextResult?.cleared === true || contextResult?.ok === true || infoResult?.cleared === true || infoResult?.ok === true,
    };
  }
  const [contextResult, infoResult] = await Promise.all([
    isAthenaDeviceContextAvailable() ? call(NativeDeviceContext, ['clearForUser', 'clearIdentity']) : null,
    isAthenaAppInfoAvailable() ? call(NativeAppInfo, ['clearForUser', 'clearIdentity']) : null,
  ]);
  return {
    available: Boolean(contextResult || infoResult),
    cleared: contextResult?.cleared === true || contextResult?.ok === true || infoResult?.cleared === true || infoResult?.ok === true,
  };
}

function cleanInstallationId(value) {
  return safeString(value, 180).match(/^[A-Za-z0-9._:-]{8,180}$/)?.[0] || '';
}

function cleanInstallationProof(value) {
  // O proof é opaco e só segue diretamente para o Worker autenticado. Ele
  // nunca entra em diagnósticos, eventos ou storage web.
  const proof = safeString(value, 2048);
  return /^[A-Za-z0-9._~+/=-]{16,2048}$/.test(proof) ? proof : '';
}

export async function getAthenaNativeInstallation() {
  if (!isNativeAndroid()) return null;
  // Registration proof is intentionally a separate native API. An older APK
  // can return public installation details but must never be treated as
  // eligible for authenticated Worker registration without the opaque proof.
  const registration = isAthenaAppInfoAvailable()
    ? await call(NativeAppInfo, ['getRegistration'])
    : null;
  const installation = (isAthenaAppInfoAvailable()
    ? await call(NativeAppInfo, ['getInstallation', 'getInfo'])
    : null) || (isAthenaDeviceContextAvailable()
    ? await call(NativeDeviceContext, ['getInstallation'])
    : null);
  const value = {
    ...(installation && typeof installation === 'object' ? installation : {}),
    ...(registration && typeof registration === 'object' ? registration : {}),
  };
  const installationId = cleanInstallationId(value.installationId || value.installation_id || value.deviceId || value.device_id);
  const installationProof = cleanInstallationProof(value.installationProof || value.installation_proof || value.proof);
  return installationId && installationProof ? { installationId, installationProof } : null;
}

/** Safe version/capability values suitable for UI and Worker negotiation. */
export async function getAthenaRuntimeCapabilities() {
  const deviceCapabilities = await getAthenaDeviceContextCapabilities();
  // getInfo is the authoritative version surface. Calling getCapabilities
  // first can return a valid feature list without versionCode/versionName,
  // which would incorrectly block native registration and bootstrap.
  let appInfo = null;
  let appCapabilityInfo = null;
  if (isAthenaAppInfoAvailable()) {
    appInfo = await call(NativeAppInfo, ['getInfo']);
    appCapabilityInfo = await call(NativeAppInfo, ['getCapabilities']);
  }
  const appMetadata = {
    ...(appCapabilityInfo && typeof appCapabilityInfo === 'object' ? appCapabilityInfo : {}),
    ...(appInfo && typeof appInfo === 'object' ? appInfo : {}),
  };
  const native = isNativeAndroid();
  const values = new Set(native
    ? [
      'native_voice_v1', 'native_push_v1', 'calendar_v1', 'files_v1', 'share_v1', 'quick_capture_v1',
      // Names are the F5 Worker contract; do not advertise legacy aliases.
      'privacy_safe_widget_v1', 'quick_settings_tile_v1', 'static_shortcuts_v1',
    ]
    : ['web_pwa_v1', 'web_notifications_v1', 'web_files_v1']);
  if (deviceCapabilities.deviceContext) values.add('device_context_v1');
  // Granular context booleans above are diagnostics/UI information. Worker
  // registration deliberately receives only device_context_v1, its approved
  // umbrella capability; it rejects invented per-signal capability labels.
  const advertised = Array.isArray(appMetadata.capabilities)
    ? appMetadata.capabilities
    : Array.isArray(appMetadata.capabilities?.features)
      ? appMetadata.capabilities.features
      : Array.isArray(appMetadata.features) ? appMetadata.features : [];
  advertised.forEach((value) => {
    const capability = safeString(value, 80);
    if (WORKER_NEGOTIABLE_CAPABILITIES.has(capability)) values.add(capability);
  });
  return Object.freeze({
    capabilityVersion: 1,
    platform: native ? 'android' : 'web',
    capabilities: Object.freeze([...values].sort()),
    deviceContext: deviceCapabilities,
    nativeVersionCode: (() => {
      const rawVersionCode = appMetadata.versionCode ?? appMetadata.version_code;
      const numeric = Number(rawVersionCode);
      return rawVersionCode !== undefined && rawVersionCode !== null && Number.isInteger(numeric) && numeric > 0
        ? numeric
        : null;
    })(),
    nativeVersion: safeString(appMetadata.versionName || appMetadata.version_name, 80) || ATHENA_VERSION_INFO.nativeVersion || null,
  });
}

export function normalizeAthenaDeviceContextEvent(raw) {
  const value = raw && typeof raw === 'object' ? raw : {};
  return {
    signal: enumValue(value.signal || value.type || value.event, DEVICE_SIGNAL_TYPES, 'snapshot'),
    snapshot: normalizeNativeSnapshot(value.snapshot ? value : { snapshot: value }),
  };
}

const DEVICE_SIGNAL_TYPES = new Set(['snapshot', 'battery', 'network', 'audio', 'app_state', 'capabilities']);
